import { Button, Image } from "antd";
import { setConfig } from "barikoiapis";
import {
  FullscreenControl,
  GeolocateControl,
  Map,
  Marker,
  NavigationControl,
  Popup,
} from "bkoi-gl";
import { debounce } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import IMG from "../../../assets/images/avatar.svg";
import EmployeeInfoItem from "../../../components/common/EmployeeInfoItem";
import InfoItem from "../../../components/common/InfoItem";
import TopCard from "../../../components/common/TopCard";
import Loader from "../../../components/ui/Loader";
import TimelineItem from "../../../components/Visit/TimelineItem";
import colors from "../../../constants/colors";
import {
  BARIKOI_API_KEY,
  dateFormat,
  dbDateFormat,
  dbTimeFormat,
  timeFormat,
} from "../../../constants/constants";
import { useGetEmployeeDetailsQuery } from "../../../features/employee/employeeApi";
import { useBariKoiApiHitMutation } from "../../../features/employeeTracking/employeeTrackingApi";
import {
  useGetVisitDetailsQuery,
  useLazyGetVisitDetailsExportQuery,
} from "../../../features/visit/visitApi";
import useError from "../../../hooks/useError";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  formatDate,
  formatTime,
  formatUTCDate,
  UTCTimeFormat,
} from "../../../lib/time";
import { capitalize } from "../../../utils/string";
import { backLink } from "../../../utils/utils";
import NotFound from "../../NotFound";

const statusStyle = {
  created: "bg-status-warning-text",
  completed: "bg-status-success-text",
  reached: "bg-status-info-text",
  started: "bg-status-info-text",
  cancelled: "bg-status-danger-text",
  rescheduled: "bg-status-dark-text",
} as const;

const lastStatus = {
  reached: "Destination",
  completed: "Visit",
} as const;

export enum VisitStatus {
  created = "status-warning",
  completed = "status-success",
  reached = "status-info",
  started = "status-info",
  cancelled = "status-danger",
  rescheduled = "status-dark",
}

export default function MyVisitDetails() {
  const location = useLocation();
  const { viewId } = useParams();
  const [isZoomMap, setIsZoomMap] = useState(true);
  const {
    data: visitDetailsData,
    isError,
    error,
    isLoading,
    isFetching,
  } = useGetVisitDetailsQuery(viewId || "");
  const { authInfo } = useAppSelector((state) => state.auth);

  const [
    getVisitDetailsExport,
    {
      data: visitDownloadData,
      isError: isVisitDetailsError,
      error: visitDetailsError,
      isLoading: isVisitDetailsLoading,
    },
  ] = useLazyGetVisitDetailsExportQuery();

  const { data: userData } = useGetEmployeeDetailsQuery(
    authInfo?.id?.toString() || "",
    {
      skip: !authInfo?.id,
    }
  );
  const [bariKoiApiHit] = useBariKoiApiHitMutation();
  const isEmailVerified =
    userData?.data?.employee?.member?.profile?.email_verified;

  useError(isError, error, "Visit details data fail");

  useError(
    isVisitDetailsError,
    visitDetailsError,
    "Get Visit report data fail"
  );

  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);
  const [currentCoordinate, setCurrentCoordinate] = useState<
    [number, number] | undefined
  >(undefined);

  const timelineLocationLog = visitDetailsData?.data?.status_change_logs?.map(
    (el) => ({
      ...el,
      new_location: {
        lat:
          el?.new_location?.lat && el?.new_location?.lat !== "null"
            ? el?.new_location?.lat
            : 0,
        lng:
          el?.new_location?.lng && el?.new_location?.lng !== "null"
            ? el?.new_location?.lng
            : 0,
        address:
          el?.new_location?.address && el?.new_location?.address !== "null"
            ? el?.new_location?.address
            : undefined,
      },
      old_location: {
        lat:
          el?.old_location?.lat && el?.old_location?.lat !== "null"
            ? el?.old_location?.lat
            : 0,
        lng:
          el?.old_location?.lng && el?.old_location?.lng !== "null"
            ? el?.old_location?.lng
            : 0,
        address:
          el?.old_location?.address && el?.old_location?.address !== "null"
            ? el?.old_location?.address
            : undefined,
      },
    })
  );

  const timelineData = timelineLocationLog?.map((el) => {
    return {
      ...el,
      address: {
        address: el?.new_location?.address,
        lng: el?.new_location?.lng,
        lat: el?.new_location?.lat,
      },
      time: `${moment(el?.created_at, dbTimeFormat).format(
        timeFormat
      )} - ${moment(el?.created_at, dbDateFormat).format(dateFormat)}`,
    };
  });

  const polygonCoordinates: {
    geo: [number, number];
    address: string;
  }[] = useMemo(
    () =>
      timelineData?.map((point) => ({
        geo: [point?.address?.lng as number, point?.address?.lat as number],
        address: point?.address?.address,
      })) || [],
    [timelineData]
  );

  setConfig({ apiKey: BARIKOI_API_KEY, version: "v2" });

  const mapContainer = useRef(null) as any;
  const map = useRef(null) as any;
  const markersRef = useRef<{ [key: string]: any }>({});

  const handleHover = useMemo(
    () =>
      debounce((lng: number, lat: number) => {
        if (map.current) {
          map.current.flyTo({
            center: [lng, lat],
            speed: 2,
          });
        }
      }, 10),
    []
  );

  const handleTimelineHover = (lng: number, lat: number, enlarge: boolean) => {
    const markerId = `${lng}-${lat}`;
    const marker = markersRef.current[markerId];

    setHoveredMarker(enlarge ? markerId : null);

    if (marker) {
      if (enlarge) {
        marker.getElement().style.transform = `${
          marker.getElement().style.transform
        } scale(1.5)`;
      } else {
        marker.getElement().style.transform = marker
          .getElement()
          .style.transform?.replace(/scale\([0-9.]+\)/g, "scale(1)");
      }
    }
  };

  useEffect(() => {
    if (isError) return;
    if (map.current && Object.keys(map.current || {}).length) return;

    if (!isLoading && !isFetching) {
      map.current = new Map({
        container: mapContainer?.current as any,
        center: polygonCoordinates?.[0]?.geo || [
          90.39017821904588, 23.719800220780733,
        ],
        zoom: 12,
        doubleClickZoom: false,
        accessToken: BARIKOI_API_KEY,
      });

      bariKoiApiHit();

      map.current.on("style.load", () => {
        map.current.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: polygonCoordinates?.map((item) => item?.geo),
            },
          },
        });

        map.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": colors?.tintNeutral,
            "line-width": 2,
          },
        });

        polygonCoordinates?.forEach((item) => {
          const markerId = `${item?.geo[0]}-${item?.geo[1]}`;
          const newMarker = new Marker({
            color: colors?.primary,
            draggable: false,
            scale: 1,
          })
            .setLngLat(item?.geo)
            .addTo(map.current);

          const popup = new Popup({
            focusAfterOpen: false,
            offset: [0, -35],
          })
            .setLngLat(item?.geo)
            .setHTML(`<p class="text-center text-header">${item?.address}</p>`);

          markersRef.current[markerId] = newMarker;

          newMarker.getElement().addEventListener("mouseenter", () => {
            setHoveredMarker(markerId);
            popup.addTo(map.current);
          });

          newMarker.getElement().addEventListener("mouseleave", () => {
            setTimeout(() => {
              setHoveredMarker(null);
              popup.remove();
            }, 100);
          });
        });

        map.current.addControl(new FullscreenControl());
        map.current.addControl(new NavigationControl());
        map.current.addControl(new GeolocateControl());
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCoordinate,
    handleHover,
    hoveredMarker,
    isError,
    isFetching,
    isLoading,
    polygonCoordinates,
  ]);

  useEffect(() => {
    if (isError) return;
    Object.entries(markersRef.current).forEach(([markerId, marker]) => {
      if (marker) {
        if (markerId === hoveredMarker) {
          marker.getElement().style.transform = `${
            marker.getElement().style.transform
          } scale(1.3)`;
        } else {
          marker.getElement().style.transform = marker
            .getElement()
            .style.transform?.replace(/scale\([0-9.]+\)/g, "scale(1)");
        }
      }
    });
  }, [hoveredMarker, isError]);

  useEffect(() => {
    if (map.current && !isLoading && polygonCoordinates?.length && isZoomMap) {
      setTimeout(() => {
        map.current.flyTo({
          center: polygonCoordinates?.[0]?.geo?.length
            ? polygonCoordinates?.[0]?.geo
            : [90.39017821904588, 23.719800220780733],
          zoom: 14,
        });

        setIsZoomMap(false);
      }, 2000);
    }
  }, [isLoading, isZoomMap, polygonCoordinates]);

  const handleReport = () => {
    getVisitDetailsExport({
      params: {
        visit_id: Number(viewId),
        business_id: authInfo?.business_id,
      },
    });
  };

  useEffect(() => {
    if (visitDownloadData) {
      const link = document.createElement("a");
      link.href = visitDownloadData;
      link.download = "visit-details.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [visitDownloadData]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout
      title="Visit"
      appTitle={`${
        location?.pathname?.includes("team") ? "Team" : "My"
      } Visit Details`}
    >
      <div
        className={`sticky inset-x-0 z-50 ${
          !isEmailVerified ? "!top-[127px]" : "top-20"
        }`}
      >
        <TopCard
          leftChildren={
            <>
              <Link to={backLink(location.pathname, 2)}>
                <Button
                  className="normal-dark-button"
                  icon={<BackButtonIcon />}
                >
                  Back
                </Button>
              </Link>
              <h4 className="text-header text-xl font-semibold leading-loose">
                {location?.pathname?.includes("team") ? "Team" : "My"} Visit
                Details
              </h4>

              <span
                className={`h-8 px-2 rounded-md justify-center items-center inline-flex text-white text-sm font-medium ${
                  statusStyle[
                    visitDetailsData?.data?.status as keyof typeof statusStyle
                  ]
                }`}
              >
                {capitalize(visitDetailsData?.data?.status || "")}
              </span>
            </>
          }
        >
          {/* <DatePicker
          defaultValue={dayjs(formatDate(), dateFormat)}
          format={dateFormat}
          style={{ width: 140 }}
          suffixIcon={<DateIcon />}
        /> */}

          <Button
            className="download-button"
            icon={<DownloadIcon />}
            loading={isVisitDetailsLoading}
            onClick={handleReport}
          >
            Export Report
          </Button>
        </TopCard>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="grid md:grid-cols-8 gap-6 mb-6">
          <div className="md:col-span-3">
            <div className="bg-white rounded-md border border-extra-light">
              <div className="p-6">
                <h4 className="text-header text-xl font-semibold mb-6">
                  Visitor Info
                </h4>
                <EmployeeInfoItem
                  img={visitDetailsData?.data?.visitor?.pro_pic || IMG}
                  name={visitDetailsData?.data?.visitor?.name}
                  id={visitDetailsData?.data?.visitor?.employee_id}
                  designation={visitDetailsData?.data?.visitor?.designation}
                  department={visitDetailsData?.data?.visitor?.department_name}
                  email={visitDetailsData?.data?.visitor?.email}
                />
              </div>

              <div className="my-4 px-6">
                <h4 className="text-header text-xl font-semibold pt-4 pb-2 border-t border-extra-light">
                  Visiting Details
                </h4>

                <ul>
                  <InfoItem title="Visit Date" titleClassName="!w-[120px]">
                    {formatDate(visitDetailsData?.data?.schedule_date)}
                  </InfoItem>
                  <InfoItem title="Visit Title" titleClassName="!w-[120px]">
                    {visitDetailsData?.data?.title}
                  </InfoItem>
                  <InfoItem title="Description" titleClassName="!w-[120px]">
                    {visitDetailsData?.data?.description || "N/A"}
                  </InfoItem>
                </ul>
              </div>

              <div>
                <h4 className="text-header text-xl font-semibold pb-6 px-6">
                  Visiting Timeline
                </h4>
                <ul>
                  {isLoading || isFetching ? (
                    <li className="h-40">
                      <Loader />
                    </li>
                  ) : (
                    timelineLocationLog?.map((item, index) => (
                      <TimelineItem
                        title={`${capitalize(item?.new_status)} ${
                          lastStatus[
                            item?.new_status as keyof typeof lastStatus
                          ] || ""
                        }`}
                        time={`${formatTime(item?.created_at)} - ${formatDate(
                          item?.created_at
                        )}`}
                        color={
                          item?.new_status === "completed"
                            ? colors.complete
                            : item?.new_status === "cancelled"
                            ? colors.darkHighlight
                            : colors.accent
                        }
                        details={item?.new_location?.address}
                        length={timelineLocationLog?.length}
                        index={index}
                        key={index}
                        onMouseEnter={() => {
                          setCurrentCoordinate([
                            item?.new_location?.lng as number,
                            item?.new_location?.lat as number,
                          ]);
                          handleHover(
                            item?.new_location?.lng as number,
                            item?.new_location?.lat as number
                          );
                          handleTimelineHover(
                            item?.new_location?.lng as number,
                            item?.new_location?.lat as number,
                            true
                          );
                        }}
                        onMouseLeave={() => {
                          handleTimelineHover(
                            item?.new_location?.lng as number,
                            item?.new_location?.lat as number,
                            false
                          );
                        }}
                      />
                    ))
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="rounded-md">
              <div className="md:col-span-5">
                {isFetching || isLoading ? (
                  <div className="mb-2">
                    <Loader />
                  </div>
                ) : null}
                <div className="rounded-md">
                  <div ref={mapContainer} style={containerStyles} />
                </div>
              </div>

              {/* <Map
                initialViewState={{
                  longitude: centerCoordinates[0],
                  latitude: centerCoordinates[1],
                  zoom: 15,
                }}
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                style={{ width: "100%", height: "500px" }}
              >
                <Marker
                  longitude={centerCoordinates[0]}
                  latitude={centerCoordinates[1]}
                  anchor="center"
                >
                  <img width={50} src={GPS_Icon} alt="" />
                </Marker>
              </Map> */}
            </div>
          </div>
        </div>
      )}

      {visitDetailsData?.data?.photos?.length ? (
        <div className="bg-white rounded-md border border-extra-light">
          <h4 className="text-header text-xl font-semibold py-2 border-b border-light/50 px-6">
            Visit Photos
          </h4>
          <div className="flex items-center gap-2 flex-wrap py-3 px-6">
            {visitDetailsData?.data?.photos?.map((photo) => (
              <Image
                width={80}
                height={80}
                style={{ objectFit: "cover", borderRadius: 2 }}
                src={photo?.photo}
              />
            ))}
          </div>
        </div>
      ) : null}

      {visitDetailsData?.data?.notes?.length ? (
        <div className="bg-white rounded-md border border-extra-light mt-6">
          <h4 className="text-header text-xl font-semibold py-2 border-b border-light/50 px-6">
            Note
          </h4>
          <div>
            {visitDetailsData?.data?.notes?.map((note) => (
              <div
                className="grid grid-cols-6 gap-4 border-b border-light/50 last:border-b-0 py-3 px-6"
                key={note?.id}
              >
                <div className="text-body !text-xs font-medium">
                  {note?.date ? (
                    <>
                      {UTCTimeFormat(note?.date)} - {formatUTCDate(note?.date)}
                    </>
                  ) : null}
                </div>
                <div className="col-span-5">
                  <span
                    className={`${
                      VisitStatus[note?.status as keyof typeof VisitStatus]
                    }`}
                  >
                    {capitalize(note?.status)}
                  </span>
                  <p className="text-body text-sm mt-2">{note?.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </SidebarLayout>
  );
}

const containerStyles = {
  width: "100%",
  height: "100vh",
  minHeight: "400px",
  overflow: "hidden",
};

// const visitTimeline = [
//   {
//     title: "Completed Visit",
//     time: "06:01 PM - 06 Nov, 2024",
//     details:
//       "House 58/Ga/6, West Rajabazar, Rajabazar, Tejgaon, Dhaka, Tejgaon, Dhaka",
//     color: colors.success.color,
//   },
//   {
//     title: "Reached Destination",
//     time: "06:01 PM - 06 Nov, 2024",
//     details:
//       "House 58/Ga/6, West Rajabazar, Rajabazar, Tejgaon, Dhaka, Tejgaon, Dhaka",
//     color: colors.accent,
//   },
// ];
